<script setup lang="ts">
import { MenuItem, MenuPopover } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import type { AccountRow } from '../internal/account-tree.ts'
import { isReconcilableAccount } from '../internal/reconciliation.ts'

const props = withDefaults(
  defineProps<{
    row: AccountRow
    canArchive: boolean
    canCreate: boolean
    canEdit: boolean
    canHide: boolean
    canReconcile?: boolean
    canSetDefault: boolean
    isDefault: boolean
    isBusy: boolean
    showOpen?: boolean
  }>(),
  { canReconcile: false, showOpen: false },
)

const emit = defineEmits<{
  'archive-request': [id: string]
  restore: [id: string]
  'add-child': [parentId: string]
  edit: [id: string]
  hide: [id: string]
  unhide: [id: string]
  'set-default': [id: string]
  reconcile: []
}>()

const { t } = useI18n()

const canMakeDefault = computed(
  () =>
    props.canSetDefault &&
    !props.isDefault &&
    props.row.accountClass === 'ASSET' &&
    props.row.isPostable &&
    !props.row.isArchived,
)
const canManageArchive = computed(() => props.canArchive && !props.row.isSystem)

function toggleHidden(): void {
  if (props.row.isHidden) emit('unhide', props.row.id)
  else emit('hide', props.row.id)
}
</script>

<template>
  <MenuPopover side="bottom" align="end">
    <template #trigger>
      <button
        type="button"
        :aria-label="t('accounts.actionsLabel', { name: row.label })"
        class="flex h-[var(--density-control)] w-[var(--density-control)] items-center justify-center rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] text-[color:var(--color-text)]"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
          <circle cx="3.5" cy="8" r="1.4" />
          <circle cx="8" cy="8" r="1.4" />
          <circle cx="12.5" cy="8" r="1.4" />
        </svg>
      </button>
    </template>
    <MenuItem v-if="showOpen" as-child>
      <RouterLink :to="{ name: 'account-detail', params: { accountId: row.id } }">
        {{ t('accounts.openAction') }}
      </RouterLink>
    </MenuItem>
    <MenuItem v-if="canMakeDefault" :disabled="isBusy" @select="emit('set-default', row.id)">
      {{ t('accounts.makeDefault') }}
    </MenuItem>
    <MenuItem v-if="canEdit && !row.isSystem" @select="emit('edit', row.id)">
      {{ t('accounts.editAction') }}
    </MenuItem>
    <MenuItem v-if="canCreate" @select="emit('add-child', row.id)">
      {{ t('accounts.addSubAccount') }}
    </MenuItem>
    <MenuItem v-if="canHide && !row.isSystem" :disabled="isBusy" @select="toggleHidden()">
      {{ row.isHidden ? t('accounts.unhideAction') : t('accounts.hideAction') }}
    </MenuItem>
    <MenuItem v-if="canReconcile && isReconcilableAccount(row)" @select="emit('reconcile')">
      {{ t('accounts.reconcileAccounts') }}
    </MenuItem>
    <template v-if="canManageArchive">
      <MenuItem v-if="row.isArchived" :disabled="isBusy" @select="emit('restore', row.id)">
        {{ t('accounts.restoreAction') }}
      </MenuItem>
      <MenuItem v-else @select="emit('archive-request', row.id)">{{ t('accounts.archiveAction') }}</MenuItem>
    </template>
  </MenuPopover>
</template>
